import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GRID_HEIGHT, GRID_HOURS, GRID_ROW_HEIGHT, hourLabel } from '../../lib/calendar';

export interface TimeGridProps {
  /** One head cell per column, already rendered. */
  heads: ReactNode;
  /** One column element per head. */
  columns: ReactNode;
  className?: string;
}

/**
 * The scrolling hour grid shared by the week and day views.
 *
 * Both had their own copy in the prototype, differing only in what a column
 * meant — a day in one, a room in the other. The frame is identical, so it is
 * written once here and the two views supply their own columns.
 *
 * `dir="ltr"` is pinned deliberately. Time runs downward and the hour gutter
 * belongs on the same side in both languages; mirroring the grid in Arabic would
 * put 07:00 in a different place from where a returning user last saw it, and
 * every calendar application they use keeps it fixed.
 */
export function TimeGrid({ heads, columns, className }: TimeGridProps): ReactElement {
  const { i18n } = useTranslation();

  return (
    <div className={['cal-wrap', className].filter(Boolean).join(' ')} dir="ltr">
      <div className="cal-head-row">
        <div className="cal-time-col-head" />
        {heads}
      </div>

      <div className="cal-body-scroll">
        <div className="cal-body">
          <div className="cal-time-col">
            {GRID_HOURS.map((hour) => (
              <div key={hour} className="cal-time-cell" style={{ height: GRID_ROW_HEIGHT }}>
                {hourLabel(hour, i18n.language)}
              </div>
            ))}
          </div>

          <div className="cal-rooms" style={{ height: GRID_HEIGHT }}>
            {columns}
          </div>
        </div>
      </div>
    </div>
  );
}
